import { createFileRoute } from "@tanstack/react-router";
import { useQueries, useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { companyApi } from "@/lib/api/services/company.api";
import { locationApi } from "@/lib/api/services/location.api";
import { userApi } from "@/lib/api/services/user.api";
import { materialApi } from "@/lib/api/services/material.api";
import { recipeApi } from "@/lib/api/services/recipe.api";
import { menuApi } from "@/lib/api/services/menu.api";
import { TrendingUp } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useLingui } from "@lingui/react";
import { AdminPageHeader } from "@/components/admin/common/AdminPageHeader";
import { AdminStatsGrid } from "@/components/admin/dashboard/AdminStatsGrid";
import { AdminQuickActionsCard } from "@/components/admin/dashboard/AdminQuickActionsCard";
import { AdminSystemOverviewCard } from "@/components/admin/dashboard/AdminSystemOverviewCard";

export const Route = createFileRoute("/(protected)/_layout/admin/")({
  component: AdminDashboard,
});

function AdminDashboard() {
  const { i18n } = useLingui();
  const { isAuthenticated, isLoading } = useAuth();

  const { data: companiesPage, isLoading: companiesLoading } = useQuery({
      queryKey: ["companies", "page1"],
    queryFn: () => companyApi.paginate({ limit: 1 }),
    enabled: isAuthenticated,
  });

  const primaryCompany = companiesPage?.data[0];

  const [
    companiesStatsQuery,
    locationsStatsQuery,
    usersStatsQuery,
    materialsStatsQuery,
    recipesStatsQuery,
    menusStatsQuery,
  ] = useQueries({
    queries: [
        {
          queryKey: ["companies-stats"],
          queryFn: () => companyApi.getGlobalStats(),
          enabled: isAuthenticated,
      },
      {
          queryKey: ["locations-stats"],
          queryFn: () => locationApi.getStats(),
          enabled: isAuthenticated,
      },
      {
          queryKey: ["users-stats"],
          queryFn: () => userApi.getStats(),
          enabled: isAuthenticated,
      },
      {
          queryKey: ["materials-stats"],
          queryFn: () => materialApi.getStats(),
          enabled: isAuthenticated,
      },
      {
          queryKey: ["recipes-stats"],
          queryFn: () => recipeApi.getStats(),
          enabled: isAuthenticated,
      },
      {
          queryKey: ["menus-stats"],
          queryFn: () => menuApi.getStats(),
          enabled: isAuthenticated,
      },
    ],
  });

  const statsLoading =
    companiesStatsQuery.isLoading ||
    locationsStatsQuery.isLoading ||
    usersStatsQuery.isLoading ||
    materialsStatsQuery.isLoading ||
    recipesStatsQuery.isLoading ||
    menusStatsQuery.isLoading;

  if (isLoading || !isAuthenticated || companiesLoading) {
    return <AdminDashboardSkeleton />;
  }

  const isSuperAdmin = (companiesStatsQuery.data?.total ?? 0) > 1;

  return (
    <div className="space-y-6">
      <AdminPageHeader
        icon={TrendingUp}
        title={i18n._("Dashboard")}
        description={
          isSuperAdmin
            ? i18n._("Manage all concepts, brands, locations, and system data")
            : i18n._("Manage your concept's data")
        }
      />

      {primaryCompany ? (
        <p className="text-sm text-(--color-text-secondary) -mt-4">
          {i18n._("Viewing: {companyName}", {
            companyName: primaryCompany.name,
          })}
        </p>
      ) : null}

      <AdminStatsGrid
        isSuperAdmin={isSuperAdmin}
        statsLoading={statsLoading}
        companiesStats={
          companiesStatsQuery.data
            ? { totalCompanies: companiesStatsQuery.data.total }
            : undefined
        }
        locationsStats={locationsStatsQuery.data}
        usersStats={usersStatsQuery.data}
        materialsStats={materialsStatsQuery.data}
        recipesStats={recipesStatsQuery.data}
        menusStats={menusStatsQuery.data}
      />

      <div className="grid gap-4 md:grid-cols-2">
        <AdminQuickActionsCard isSuperAdmin={isSuperAdmin} />
        <AdminSystemOverviewCard />
      </div>
    </div>
  );
}

function AdminDashboardSkeleton() {
  return (
    <div className="space-y-6">
      <div>
        <Skeleton className="h-8 w-56" />
        <Skeleton className="mt-2 h-4 w-72" />
      </div>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <Skeleton key={index} className="h-28 w-full rounded-lg" />
        ))}
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        {Array.from({ length: 2 }).map((_, index) => (
          <Skeleton key={index} className="h-64 w-full rounded-lg" />
        ))}
      </div>
    </div>
  );
}
